import capitalize from 'lodash/capitalize';
import { TransformType } from 'new/components/ReusableTransform/types';
import React, { useEffect } from 'react';
import {
  RESOURCE_TYPE_ATTR_TRANSFORM,
  RESOURCE_TYPE_RECORD_TRANSFORM,
} from 'js/constants/resourceTypes';
import DateUtils from '../../../lib/DateUtils';
import DocEditor from '../../elements/editors/DocEditor';
import LoadableMonacoEditor from '../../elements/editors/monaco/LoadableMonacoEditor';
import TagsEditor from '../../elements/tags/TagsEditor';
import TagsView from '../../elements/tags/TagsView';
import { useSelector } from 'react-redux';
import { fetchTransformDoc, getCodeContainer } from 'js/actions';
import DataSetRecipeTab from 'js/components/data_sets/cards/DataSetRecipeTab';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_CODE_CONTAINER_REQUEST, GET_DATA_SET_REQUEST } from 'js/constants/actionTypes';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { useDataSetAdapter } from 'new/state/data_set/adapter/useDataSetAdapter';
import { getFeatureFlagValue } from 'new/constants/featureFlags';

import s from './ReusableTxOverviewTab.module.scss';

const LANGUAGE_LABELS = Object.freeze({
  python: 'Python',
  python3: 'Python 3 (Experimental)',
  javascript: 'JavaScript',
  nexla_json: 'Nexla JSON',
  flink_sql: 'SQL',
  spark_sql: 'SQL',
});

function ReusableTxOverviewTab(props) {
  const dispatch = useAppDispatch();
  const { requestDataSetById } = useDataSetAdapter();
  const decoded = useSelector((state) => state.transforms.allTransforms.decoded);
  const loading = useIsLoading(GET_CODE_CONTAINER_REQUEST, GET_DATA_SET_REQUEST);
  const dataSets = useSelector((state) => state.entities.dataSets);

  const {
    data,
    data: { id, output_type, created_at, updated_at },
    readOnly,
    doc,
    isLoadingDoc,
    updateTransformDoc,
  } = props;

  const dataSet = dataSets[data.data_sets?.[0]];

  useEffect(() => {
    dispatch(fetchTransformDoc(props.data.id));
    if (!props.data.decoded) {
      dispatch(getCodeContainer(props.data.id));
    }
    if (!dataSet?.custom_config && dataSet?.id) {
      requestDataSetById(dataSet?.id);
    }
  }, []);

  let createdDate = DateUtils.convertDateStringToFormat(created_at);
  let lastUpdate = DateUtils.convertDateStringToFormat(updated_at);
  let decodedCode = props.data.decoded || decoded[props.data.id]?.decoded;
  let codeLanguage = props.data.codeLanguage || decoded[props.data.id]?.codeLanguage;
  let isAttributeTx = output_type === TransformType.ATTRIBUTE;
  let code_type = (isAttributeTx ? props.data.code_type : codeLanguage) || 'json';

  let codeEditorMode = 'json';
  let displayLanguage = 'Nexla JSON';

  if (code_type === 'javascript' || code_type.includes('python')) {
    codeEditorMode = code_type;
    displayLanguage = LANGUAGE_LABELS[code_type];
  }

  const tagsView =
    readOnly === true ? (
      <TagsView data={props.data} />
    ) : (
      <TagsEditor
        id={id}
        resourceType={isAttributeTx ? RESOURCE_TYPE_ATTR_TRANSFORM : RESOURCE_TYPE_RECORD_TRANSFORM}
      />
    );

  return (
    <div className="tab-content preview-tab">
      <div className="detail-view-two-col">
        <div className="detail-view-main-col">
          <DocEditor
            data={data}
            doc={doc}
            isLoading={isLoadingDoc}
            updateDoc={updateTransformDoc}
          />

          {!data.reusable ? (
            <DataSetRecipeTab data={dataSet} />
          ) : (
            <>
              <div className={s.transform_code_header}>
                <span className={s.title}>Transform Code &gt;&gt; {displayLanguage}</span>
              </div>
              {loading && !decodedCode ? (
                <CenteredLoader />
              ) : (
                <div className={s.monaco_editor_container}>
                  <LoadableMonacoEditor
                    language={codeEditorMode}
                    height={500}
                    value={decodedCode}
                    theme="light"
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="detail-view-side-col">
          <section style={{ padding: '0 16px' }}>
            <div className="detail-row">
              <div className="detail-col">
                <div className="detail-key">Resource ID</div>
                <div className="detail-value">{id}</div>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-col">
                <div className="detail-key">Language</div>
                <div className="detail-value">{displayLanguage}</div>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-col">
                <div className="detail-key">Created On</div>
                <div className="detail-value">{createdDate}</div>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-col">
                <div className="detail-key">Last Updated On</div>
                <div className="detail-value">{lastUpdate}</div>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-col">
                <div className="detail-key">Tags</div>
                <div className="detail-value">{tagsView}</div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default ReusableTxOverviewTab;
